
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MapPin, Clock, DollarSign, Facebook, ExternalLink, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { formatRelativeDate, formatLocation, formatProjectType, formatPayType, getJobStatusBadgeVariant, formatRole } from '@/utils/formatters'

export interface Job {
  id: string
  title: string
  company?: string
  project_type: string
  location: string
  description: string
  requirements?: string
  roles?: string[]
  created_at: string
  updated_at: string
  status: string
  user_id: string
  view_count: number
  pay_type?: string
  custom_pay_amount?: string
}

interface JobCardProps {
  job: Job
  variant?: 'grid' | 'list'
}

export function JobCard({ job, variant = 'grid' }: JobCardProps) {
  const navigate = useNavigate()

  // Format budget display
  const formatPay = () => {
    if (job.custom_pay_amount) {
      return job.custom_pay_amount
    }
    return formatPayType(job.pay_type || '')
  }

  // Format view count display
  const formatViewCount = () => {
    const count = job.view_count || 0
    return count === 1 ? '1 view' : `${count.toLocaleString()} views`
  }

  if (variant === 'list') {
    return (
      <Card
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => navigate(`/job/${job.id}`)}
      >
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left section - Main content */}
            <div className="flex-1 min-w-0">
              {/* Title section - fixed height */}
              <div className="flex justify-between items-start gap-4 mb-2 min-h-[3rem]">
                <CardTitle className="text-xl line-clamp-2 leading-7">{job.title}</CardTitle>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatRelativeDate(job.created_at)}
                  </div>
                </div>
              </div>

              {/* Badges section - fixed height */}
              <div className="flex gap-2 flex-wrap mb-3 min-h-[2.5rem] items-start">
                {job.company && (
                  <Badge variant="secondary">
                    {job.company}
                  </Badge>
                )}
                <Badge variant="outline">{formatProjectType(job.project_type)}</Badge>
                {job.roles && job.roles.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {job.roles.slice(0, 2).map((role, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {formatRole(role)}
                      </Badge>
                    ))}
                    {job.roles.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{job.roles.length - 2} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Description section - fixed height */}
              <div className="mb-2 min-h-[3rem]">
                <p className="text-sm text-muted-foreground line-clamp-3 leading-5">{job.description}</p>
              </div>
            </div>

            {/* Right section - Details - fixed layout */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="space-y-1 mb-4">
                {/* Location - always present */}
                <div className="flex items-center text-sm text-muted-foreground min-h-[1.2rem]">
                  <MapPin className="w-3 h-3 mr-1" />
                  {formatLocation(job.location)}
                </div>
                {/* Budget - reserve space */}
                <div className="flex items-center text-sm text-muted-foreground min-h-[1.2rem]">
                  <DollarSign className="w-3 h-3 mr-1" />
                  {formatPay()}
                </div>
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <Eye className="w-3 h-3 mr-1" />
                {formatViewCount()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Grid variant
  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={() => navigate(`/job/${job.id}`)}
    >
      <CardHeader className="p-4">
        {/* Title section - fixed height */}
        <div className="flex justify-between items-start gap-2 mb-2 min-h-[3rem]">
          <CardTitle className="text-lg line-clamp-2 leading-6">{job.title}</CardTitle>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="w-3 h-3 mr-1" />
              {formatRelativeDate(job.created_at)}
            </div>
            <div className="flex items-center text-xs text-muted-foreground">
              <Eye className="w-3 h-3 mr-1" />
              {formatViewCount()}
            </div>
          </div>
        </div>
        
        {/* Badges section - fixed height */}
        <div className="flex gap-2 flex-wrap min-h-[2.5rem] items-start mb-3">
          {job.company && (
            <Badge variant="secondary" className="text-xs">
              {job.company}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">{formatProjectType(job.project_type)}</Badge>
          {job.roles && job.roles.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {job.roles.slice(0, 2).map((role, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {formatRole(role)}
                </Badge>
              ))}
              {job.roles.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{job.roles.length - 2} more
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 p-4 pt-0">
        {/* Description section - fixed height */}
        <div className="mb-2 min-h-[3rem]">
          <p className="text-sm text-muted-foreground line-clamp-3 leading-5">{job.description}</p>
        </div>
        
        {/* Location and budget section - fixed layout */}
        <div className="space-y-1">
          {/* Location - always present */}
          <div className="flex items-center text-sm text-muted-foreground min-h-[1.2rem]">
            <MapPin className="w-3 h-3 mr-1" />
            {formatLocation(job.location)}
          </div>
          {/* Budget - reserve space */}
          <div className="flex items-center text-sm text-muted-foreground min-h-[1.2rem]">
            <DollarSign className="w-3 h-3 mr-1" />
            {formatPay()}
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0">
        <Button variant="outline" size="sm" className="w-full">
          View Details
        </Button>
      </CardFooter>
    </Card>
  )
}
